import { useState } from 'react';

import { CellClassParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppDispatch } from '@/app/hooks';
import { addToast } from '@/app/slices/toast';
import { TOG_CsrDay7 } from '@/rtk-query.gql';

type CsrDay7ActionsProps = {
  csrDay7Data: TOG_CsrDay7[];
  gridRef: React.RefObject<AgGridReact>;
  handleFrozen: () => void;
  handleImplanted: () => void;
  handleDiscarded: () => void;
  refetch: () => void;
  disabled: boolean;
};
export const CsrDay7Actions = ({
  gridRef,
  handleFrozen,
  handleImplanted,
  handleDiscarded,
  refetch,
  disabled,
}: CsrDay7ActionsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [params, setParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(false);

  const handleNavigateRecipSummary = () => {
    const currentParams = new URLSearchParams(params);
    const queryString = currentParams.toString();

    navigate(`/day-7-transfer-csr/recip-summary?${queryString}`);
  };

  const handleNavigateSummary = () => {
    const currentParams = new URLSearchParams(params);
    const queryString = currentParams.toString();

    navigate(`/day-7-transfer-csr/summary?${queryString}`);
  };

  const handleFinalUpdateOvadrive = async () => {
    setIsLoading(true);
    try {
      dispatch(
        addToast({
          severity: 'success',
          summary: t('common.success'),
          detail: t('common.updatedSuccessfully'),
          life: 3000,
        }),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleShadeRows = () => {
    if (!gridRef.current?.api) return;

    const cellClasses = new Map<string, string>();
    let currentColor = 0;
    let previousKey = '';

    gridRef.current.api.forEachNodeAfterFilterAndSort((node) => {
      const plateNumber = node?.data?.plateNumber;
      const fertilizationId = node?.data?.fertilizationId;
      const semenType = node?.data?.st;

      const currentKey = plateNumber || `${fertilizationId}-${semenType}`;

      if (previousKey && currentKey !== previousKey) {
        currentColor = 1 - currentColor;
      }

      cellClasses.set(
        node.id as string,
        currentColor ? 'bg-tog-light-green' : 'bg-tog-light-purple',
      );
      previousKey = currentKey;
    });

    const updatedColumns = gridRef.current.api.getColumns()?.map((col) => {
      const colDef = col.getColDef();

      return {
        ...colDef,
        cellClassRules: {
          ...(colDef.cellClassRules || {}),
          'bg-tog-light-purple': (params: CellClassParams) =>
            cellClasses.get(params.node.id as string) === 'bg-tog-light-purple',
          'bg-tog-light-green': (params: CellClassParams) =>
            cellClasses.get(params.node.id as string) === 'bg-tog-light-green',
        },
      };
    });

    if (updatedColumns) {
      gridRef.current.api.setGridOption('columnDefs', updatedColumns);
    }

    gridRef.current.api.refreshCells();
  };

  return (
    <div className="flex flex-wrap gap-3">
      <Button icon="pi pi-refresh" onClick={refetch} />
      <Button
        label={t('embryo.frozen')}
        onClick={handleFrozen}
        disabled={disabled}
      />
      <Button
        label={t('embryo.implanted')}
        severity="success"
        onClick={handleImplanted}
        disabled={disabled}
      />
      <Button
        label={t('embryo.discarded')}
        severity="danger"
        onClick={handleDiscarded}
        disabled={disabled}
      />
      <Button
        label={t('recipients.recipSummary')}
        onClick={handleNavigateRecipSummary}
      />
      <Button label={t('common.summary')} onClick={handleNavigateSummary} />
      <Button label={t('mating.shade')} onClick={handleShadeRows} />
      <Button label={t('ovaDrive.midProcessUpdateOvadrive')} />
      <Button
        label={t('ovaDrive.finalUpdateOvadrive')}
        severity="success"
        loading={isLoading}
        onClick={handleFinalUpdateOvadrive}
      />
    </div>
  );
};
